import { useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import {
  Badge,
  Empty,
  Field,
  FormGrid,
  Modal,
  Notice,
  Page,
  Panel,
  StatTile,
  Table,
  TileRow,
  fmtNumber,
  usePoll,
} from '../components/ui'
import type {
  Campaign,
  CampaignSummary,
  DidPool,
  ListDryRun,
  ListSummary,
  Trunk,
} from '../types'

const WEEKDAYS = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
  { n: 6, label: 'Sat' },
  { n: 7, label: 'Sun' },
]

const BLANK = {
  name: '',
  description: '',
  dial_mode: 'manual',
  active: false,
  trunk_id: null as string | null,
  did_pool_id: null as string | null,
  dial_timeout_sec: 30,
  wrapup_sec: 30,
  recording_enabled: true,
  amd_enabled: false,
  max_ratio: 1,
  target_abandon_pct: 2.5,
  curfew_start: '09:00',
  curfew_end: '20:00',
  allowed_weekdays: [1, 2, 3, 4, 5],
  max_attempts: 5,
  recycle_after_hours: 24,
  script: '',
}

export default function Campaigns() {
  const campaigns = usePoll<CampaignSummary[]>(() => api.campaigns(), 0, [])
  const [refreshKey, setRefreshKey] = useState(0)
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [openLists, setOpenLists] = useState<CampaignSummary | null>(null)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success'; text: string } | null>(null)

  const data = usePoll<CampaignSummary[]>(() => api.campaigns(), 0, [refreshKey])
  const rows = data.data ?? campaigns.data ?? []

  const toggle = async (c: CampaignSummary) => {
    try {
      await api.setCampaignActive(c.id, !c.active)
      setNotice({
        kind: 'success',
        text: `${c.name} ${!c.active ? 'started' : 'stopped'}.`,
      })
      setRefreshKey((k) => k + 1)
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Failed' })
    }
  }

  const totals = rows.reduce(
    (a, c) => ({
      leads: a.leads + c.leads,
      dialable: a.dialable + c.dialable,
      active: a.active + (c.active ? 1 : 0),
    }),
    { leads: 0, dialable: 0, active: 0 },
  )

  return (
    <Page
      title="Campaigns"
      subtitle="Dialing configuration, lists and pacing"
      actions={
        <button className="primary" onClick={() => setEditing('new')}>
          New campaign
        </button>
      }
    >
      {notice && (
        <Notice kind={notice.kind} onDismiss={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <TileRow>
        <StatTile label="Campaigns" value={rows.length} />
        <StatTile label="Running" value={totals.active} tone={totals.active > 0 ? 'ok' : undefined} />
        <StatTile label="Leads loaded" value={fmtNumber(totals.leads)} />
        <StatTile label="Dialable now" value={fmtNumber(totals.dialable)} tone="live" />
      </TileRow>

      <Panel>
        {rows.length === 0 ? (
          <Empty
            message="No campaigns yet."
            hint="A campaign holds the lists you dial, the trunk you dial through, and the rules that govern pacing."
          />
        ) : (
          <Table head={['', 'Campaign', 'Mode', 'Lists', 'Leads', 'Dialable', '']}>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  <button
                    className={c.active ? 'pill-toggle on' : 'pill-toggle'}
                    onClick={() => toggle(c)}
                    title={c.active ? 'Stop this campaign' : 'Start this campaign'}
                  >
                    <span className="pill-dot" />
                    {c.active ? 'Running' : 'Stopped'}
                  </button>
                </td>
                <td>
                  <button className="link strong" onClick={() => setEditing(c.id)}>
                    {c.name}
                  </button>
                  {c.description && <div className="muted small">{c.description}</div>}
                </td>
                <td>
                  <Badge tone={c.dial_mode === 'predictive' ? 'warn' : 'muted'}>{c.dial_mode}</Badge>
                </td>
                <td>{c.lists}</td>
                <td>{fmtNumber(c.leads)}</td>
                <td className={c.dialable === 0 && c.active ? 'danger-text' : ''}>
                  {fmtNumber(c.dialable)}
                </td>
                <td className="row-actions">
                  <button className="link" onClick={() => setOpenLists(c)}>
                    Lists
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      {editing && (
        <CampaignEditor
          id={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            setRefreshKey((k) => k + 1)
            setNotice({ kind: 'success', text: 'Campaign saved.' })
          }}
        />
      )}

      {openLists && (
        <ListsModal
          campaign={openLists}
          onClose={() => {
            setOpenLists(null)
            setRefreshKey((k) => k + 1)
          }}
        />
      )}
    </Page>
  )
}

// ======================================================== campaign editor ====

function CampaignEditor({
  id,
  onClose,
  onSaved,
}: {
  id: string | 'new'
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({ ...BLANK })
  const [trunks, setTrunks] = useState<Trunk[]>([])
  const [pools, setPools] = useState<DidPool[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(id === 'new')

  useEffect(() => {
    api.trunks().then(setTrunks).catch(() => setTrunks([]))
    api.didPools().then(setPools).catch(() => setPools([]))
    if (id !== 'new') {
      api
        .campaign(id)
        .then((c: Campaign) => {
          setForm({
            ...c,
            description: c.description ?? '',
            script: c.script ?? '',
            curfew_start: c.curfew_start.slice(0, 5),
            curfew_end: c.curfew_end.slice(0, 5),
          })
          setLoaded(true)
        })
        .catch((e) => setError(e.message))
    }
  }, [id])

  const set = <K extends keyof typeof form,>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const payload = {
        ...form,
        description: form.description || null,
        script: form.script || null,
        max_ratio: Number(form.max_ratio),
        target_abandon_pct: Number(form.target_abandon_pct),
      }
      if (id === 'new') await api.createCampaign(payload)
      else await api.updateCampaign(id, payload)
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  const automated = form.dial_mode === 'power' || form.dial_mode === 'predictive'

  return (
    <Modal title={id === 'new' ? 'New campaign' : form.name || 'Campaign'} onClose={onClose} wide>
      {!loaded ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          {error && <Notice kind="error">{error}</Notice>}

          <FormGrid>
            <Field label="Name">
              <input value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />
            </Field>
            <Field label="Dial mode">
              <select value={form.dial_mode} onChange={(e) => set('dial_mode', e.target.value)}>
                <option value="manual">Manual — agent dials each number</option>
                <option value="preview">Preview — agent sees the lead, then dials</option>
                <option value="power">Power — fixed lines per agent</option>
                <option value="predictive">Predictive — paced against availability</option>
                <option value="inbound">Inbound — receives calls only</option>
              </select>
            </Field>

            <Field label="Description" hint="Shown to supervisors, not to agents.">
              <input
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </Field>

            <Field label="Outbound trunk" hint={automated ? 'Required for automated dialing.' : undefined}>
              <select
                value={form.trunk_id ?? ''}
                onChange={(e) => set('trunk_id', e.target.value || null)}
              >
                <option value="">— none —</option>
                {trunks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.host})
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Caller ID pool" hint="Numbers presented to the called party.">
              <select
                value={form.did_pool_id ?? ''}
                onChange={(e) => set('did_pool_id', e.target.value || null)}
              >
                <option value="">— none —</option>
                {pools.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.numbers} numbers)
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Ring timeout" hint="Seconds before a no-answer.">
              <input
                type="number"
                value={form.dial_timeout_sec}
                onChange={(e) => set('dial_timeout_sec', +e.target.value)}
              />
            </Field>
            <Field label="Wrap-up" hint="Seconds an agent gets to disposition.">
              <input
                type="number"
                value={form.wrapup_sec}
                onChange={(e) => set('wrapup_sec', +e.target.value)}
              />
            </Field>
          </FormGrid>

          <h3 className="form-section">Pacing</h3>
          <FormGrid>
            <Field
              label="Lines per agent"
              hint={automated ? 'How many numbers are dialed for each available agent.' : 'Only used by power and predictive.'}
            >
              <input
                type="number"
                step="0.1"
                min="1"
                max="10"
                value={form.max_ratio}
                onChange={(e) => set('max_ratio', +e.target.value)}
                disabled={!automated}
              />
            </Field>
            <Field
              label="Abandon target %"
              hint="The federal safe-harbour ceiling is 3.0%. Values above it are refused."
            >
              <input
                type="number"
                step="0.1"
                min="0"
                max="3"
                value={form.target_abandon_pct}
                onChange={(e) => set('target_abandon_pct', +e.target.value)}
                disabled={!automated}
              />
            </Field>
            <Field label="Max attempts per lead">
              <input
                type="number"
                value={form.max_attempts}
                onChange={(e) => set('max_attempts', +e.target.value)}
              />
            </Field>
            <Field label="Recycle after (hours)" hint="How long before an unanswered lead is dialed again.">
              <input
                type="number"
                value={form.recycle_after_hours}
                onChange={(e) => set('recycle_after_hours', +e.target.value)}
              />
            </Field>
          </FormGrid>

          <h3 className="form-section">
            Calling window
            <span className="form-section-note">
              Evaluated in the called party&apos;s local time, not the server&apos;s.
            </span>
          </h3>
          <FormGrid>
            <Field label="Earliest">
              <input
                type="time"
                value={form.curfew_start}
                onChange={(e) => set('curfew_start', e.target.value)}
              />
            </Field>
            <Field label="Latest">
              <input
                type="time"
                value={form.curfew_end}
                onChange={(e) => set('curfew_end', e.target.value)}
              />
            </Field>
          </FormGrid>

          <div className="weekday-row">
            {WEEKDAYS.map((d) => (
              <button
                key={d.n}
                className={form.allowed_weekdays.includes(d.n) ? 'chip on' : 'chip'}
                onClick={() =>
                  set(
                    'allowed_weekdays',
                    form.allowed_weekdays.includes(d.n)
                      ? form.allowed_weekdays.filter((x) => x !== d.n)
                      : [...form.allowed_weekdays, d.n].sort(),
                  )
                }
              >
                {d.label}
              </button>
            ))}
          </div>

          <h3 className="form-section">Options</h3>
          <div className="check-row">
            <label>
              <input
                type="checkbox"
                checked={form.recording_enabled}
                onChange={(e) => set('recording_enabled', e.target.checked)}
              />
              Record calls
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.amd_enabled}
                onChange={(e) => set('amd_enabled', e.target.checked)}
              />
              Answering machine detection
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
              />
              Active
            </label>
          </div>

          <Field label="Agent script" hint="Shown on the agent station during the call.">
            <textarea
              rows={5}
              value={form.script}
              onChange={(e) => set('script', e.target.value)}
              placeholder="Hi {first_name}, this is … calling from …"
            />
          </Field>

          <div className="modal-actions">
            <button className="ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="primary" onClick={save} disabled={busy || !form.name.trim()}>
              {busy ? 'Saving…' : 'Save campaign'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

// ============================================================ lists modal ====

function ListsModal({ campaign, onClose }: { campaign: CampaignSummary; onClose: () => void }) {
  const [lists, setLists] = useState<ListSummary[]>([])
  const [inspecting, setInspecting] = useState<ListSummary | null>(null)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const load = () => api.lists(campaign.id).then(setLists).catch(() => setLists([]))
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id])

  const create = async () => {
    if (!name.trim()) return
    try {
      await api.createList(campaign.id, name.trim())
      setName('')
      load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create the list')
    }
  }

  const remove = async (l: ListSummary) => {
    if (
      !confirm(
        `Delete "${l.name}" and its ${l.total.toLocaleString()} leads? This cannot be undone.`,
      )
    )
      return
    await api.deleteList(l.id)
    load()
  }

  return (
    <Modal title={`Lists — ${campaign.name}`} onClose={onClose} wide>
      {error && <Notice kind="error" onDismiss={() => setError(null)}>{error}</Notice>}

      <div className="inline-form">
        <input
          placeholder="New list name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && create()}
        />
        <button className="primary" onClick={create} disabled={!name.trim()}>
          Add list
        </button>
      </div>

      {lists.length === 0 ? (
        <Empty
          message="No lists in this campaign."
          hint="Create a list, then load leads into it from the Leads screen."
        />
      ) : (
        <Table head={['List', 'Total', 'Dialable', 'Contacted', 'Retired', '']}>
          {lists.map((l) => (
            <tr key={l.id}>
              <td className="strong">{l.name}</td>
              <td>{fmtNumber(l.total)}</td>
              <td>{fmtNumber(l.dialable)}</td>
              <td>{fmtNumber(l.contacted)}</td>
              <td className="muted">{fmtNumber(l.retired)}</td>
              <td className="row-actions">
                <button className="link" onClick={() => setInspecting(l)}>
                  Source &amp; dry run
                </button>
                <button className="link danger-text" onClick={() => remove(l)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </Table>
      )}

      <p className="muted small">
        Leads are loaded from the Leads screen — choose a list there and upload a CSV.
      </p>

      {inspecting && (
        <ListSourceModal
          list={inspecting}
          onClose={() => {
            setInspecting(null)
            load()
          }}
        />
      )}
    </Modal>
  )
}

// ================================================= list source and dry run ====

/**
 * Where a list's data came from, and how much of it can actually be dialled.
 *
 * The two belong on one screen because they are the same question asked twice.
 * A purchased list is priced by the record, but only the records that pass the
 * gate have any value, and the gap between those numbers is usually not small.
 * Finding it by dialling and watching the blocks accumulate costs money, burns
 * numbers, and puts calls on the wire the operator has not yet decided they can
 * defend.
 *
 * The dry run samples randomly rather than taking the first N. Lists arrive
 * sorted, and the first thousand rows of a file ordered by state or by incident
 * date are not the list.
 */
function ListSourceModal({ list, onClose }: { list: ListSummary; onClose: () => void }) {
  const [result, setResult] = useState<ListDryRun | null>(null)
  const [source, setSource] = useState('')
  const [use, setUse] = useState('')
  const [contract, setContract] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const run = async (sample: number) => {
    setBusy(true)
    setError(null)
    try {
      const r = await api.listDryRun(list.id, sample)
      setResult(r)
      setSource(r.provenance.data_source ?? '')
      setUse(r.provenance.dppa_permitted_use ?? '')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not run the dry run')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void run(200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.id])

  const save = async (certify: boolean) => {
    setBusy(true)
    setError(null)
    try {
      await api.setListProvenance(list.id, {
        data_source: source,
        dppa_permitted_use: use,
        source_contract_ref: contract || undefined,
        certify,
      })
      setSaved(certify ? 'List certified.' : 'Saved.')
      void run(result?.sampled ?? 200)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={`Source & dry run — ${list.name}`} onClose={onClose} wide>
      {error && <Notice kind="error" onDismiss={() => setError(null)}>{error}</Notice>}
      {saved && <Notice kind="success" onDismiss={() => setSaved(null)}>{saved}</Notice>}

      {result && !result.provenance.certified && (
        <Notice kind="warn">
          This list has not been certified, so the gate will refuse every call on it while
          <code> compliance.require_list_provenance </code> is on.
        </Notice>
      )}

      <h3>Where the data came from</h3>
      <FormGrid>
        <Field
          label="Data source"
          hint="Name the actual product, not the vendor. &quot;LexisNexis&quot; is not an answer; &quot;LexisNexis Police Reports (BuyCrash), Ohio&quot; is."
        >
          <input value={source} onChange={(e) => setSource(e.target.value)} />
        </Field>
        <Field
          label="Permitted use"
          hint="The use certified when the data was acquired. If the source is DPPA-covered this is the field that matters."
        >
          <textarea rows={3} value={use} onChange={(e) => setUse(e.target.value)} />
        </Field>
        <Field label="Contract or order reference">
          <input value={contract} onChange={(e) => setContract(e.target.value)} />
        </Field>
      </FormGrid>

      <div className="modal-actions">
        <button className="ghost" onClick={() => save(false)} disabled={busy}>
          Save
        </button>
        <button
          className="primary"
          onClick={() => save(true)}
          disabled={busy || !source.trim() || !use.trim()}
        >
          Save and certify
        </button>
      </div>

      <h3>How much of it is dialable</h3>
      {!result ? (
        <Empty message={busy ? 'Running…' : 'No dry run yet'} />
      ) : (
        <>
          <TileRow>
            <StatTile
              label="Dialable now"
              value={`${result.dialable_pct}%`}
              tone={result.dialable_pct >= 50 ? 'ok' : 'warn'}
              sub={`${fmtNumber(result.counts.allow)} of ${fmtNumber(result.sampled)} sampled`}
            />
            <StatTile
              label="Callable later"
              value={`${result.deferred_pct}%`}
              sub="holds, curfew, daily caps"
            />
            <StatTile
              label="Not callable"
              value={`${result.blocked_pct}%`}
              tone={result.blocked_pct > 50 ? 'danger' : undefined}
              sub="as currently configured"
            />
            <StatTile label="Leads in list" value={fmtNumber(result.leads_total)} />
          </TileRow>

          {Object.keys(result.reasons).length > 0 && (
            <Table head={['Reason', 'Leads in sample']}>
              {Object.entries(result.reasons)
                .sort((a, b) => b[1] - a[1])
                .map(([reason, n]) => (
                  <tr key={reason}>
                    <td className="mono">{reason}</td>
                    <td>{fmtNumber(n)}</td>
                  </tr>
                ))}
            </Table>
          )}

          <p className="muted small">{result.note}</p>
          <div className="inline-form">
            <button className="ghost" onClick={() => run(200)} disabled={busy}>
              Re-run (200)
            </button>
            <button className="ghost" onClick={() => run(1000)} disabled={busy}>
              Larger sample (1,000)
            </button>
          </div>
          <p className="muted small">
            The dry run performs real number lookups for anything not already cached, so a larger
            sample costs what a scrub of that many numbers costs.
          </p>
        </>
      )}
    </Modal>
  )
}
